import React, { useEffect, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface TreasuryReport {
  cashBalance: number;
  bankBalance: number;
  clientReceivables: number;
  supplierPayables: number;
  netTreasury: number;
}

interface MarginReport {
  productId: string;
  productName: string;
  salesPrice: number;
  purchasePrice: number;
  grossMargin: number;
  marginPercentage: number;
  quantitySold: number;
  totalMargin: number;
}

interface JournalEntry {
  id: number;
  date: string;
  number: string;
  name: string;
  description: string;
  amountHT: number;
  tva: number;
  amountTTC: number;
  paymentStatus: string;
  paymentType?: string;
}

interface CashFlowForecast {
  date: string;
  expectedInflows: number;
  expectedOutflows: number;
  netFlow: number;
  cumulativeBalance: number;
}

export default function FinancialReporting() {
  const [activeTab, setActiveTab] = useState<'treasury' | 'margins' | 'journals' | 'forecasting'>('treasury');
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');

  const [treasuryReport, setTreasuryReport] = useState<TreasuryReport | null>(null);
  const [marginReports, setMarginReports] = useState<MarginReport[]>([]);
  const [purchaseJournal, setPurchaseJournal] = useState<JournalEntry[]>([]);
  const [salesJournal, setSalesJournal] = useState<JournalEntry[]>([]);
  const [cashFlowForecasts, setCashFlowForecasts] = useState<CashFlowForecast[]>([]);
  const [loading, setLoading] = useState(true);

  /* ================= CHARGEMENT DES DONNÉES ================= */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [treasuryRes, marginsRes, purchaseRes, salesRes, forecastRes] = await Promise.all([
          getResources(`reporting/treasury`),
          getResources(`reporting/margins`),
          getResources(`reporting/purchase-journal`),
          getResources(`reporting/sales-journal`),
          getResources(`reporting/cashflow-forecast?period=${selectedPeriod}`)
        ]);

        setTreasuryReport(treasuryRes.data);
        setMarginReports(marginsRes.data);
        setPurchaseJournal(purchaseRes.data);
        setSalesJournal(salesRes.data);
        setCashFlowForecasts(forecastRes.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des rapports financiers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedPeriod]);

  /* ================= CALCULS DÉRIVÉS ================= */
  const totalGrossMargin = marginReports.reduce((sum, r) => sum + r.totalMargin, 0);
  const averageMarginPercentage = marginReports.length > 0
    ? marginReports.reduce((sum, r) => sum + r.marginPercentage, 0) / marginReports.length
    : 0;

  if (loading || !treasuryReport) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des rapports financiers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Reporting Financier</h2>
            <p className="text-purple-100 mt-1">Indicateurs clés et analyses de performance</p>
          </div>
          <div className="text-right">
            <p className="text-purple-100">Trésorerie Nette</p>
            <p className="text-3xl font-bold">{treasuryReport.netTreasury.toFixed(2)} GNF</p>
          </div>
        </div>
      </div>

      {/* Indicateurs clés */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <i className="ri-money-euro-circle-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Caisse</p>
              <p className="text-2xl font-bold text-gray-900">{treasuryReport.cashBalance.toFixed(2)} GNF</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <i className="ri-bank-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Banque</p>
              <p className="text-2xl font-bold text-gray-900">{treasuryReport.bankBalance.toFixed(2)} GNF</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600">
              <i className="ri-arrow-up-circle-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Créances Clients</p>
              <p className="text-2xl font-bold text-gray-900">{treasuryReport.clientReceivables.toFixed(2)} GNF</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <i className="ri-arrow-down-circle-line text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Dettes Fournisseurs</p>
              <p className="text-2xl font-bold text-gray-900">{treasuryReport.supplierPayables.toFixed(2)} GNF</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('treasury')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'treasury'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-safe-line mr-2"></i>
              Tableau de Bord Trésorerie
            </button>
            <button
              onClick={() => setActiveTab('margins')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'margins'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-percentage-line mr-2"></i>
              Rapport de Marge Brute
            </button>
            <button
              onClick={() => setActiveTab('journals')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'journals'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-book-line mr-2"></i>
              Journaux Comptables
            </button>
            <button
              onClick={() => setActiveTab('forecasting')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'forecasting'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-line-chart-line mr-2"></i>
              Prévisionnel des Flux
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* === TRÉSORERIE === */}
          {activeTab === 'treasury' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Tableau de Bord de la Trésorerie</h3>
                <div className="flex items-center space-x-4">
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="current_month">Mois Actuel</option>
                    <option value="last_month">Mois Précédent</option>
                    <option value="current_quarter">Trimestre Actuel</option>
                    <option value="current_year">Année Actuelle</option>
                  </select>
                  <button className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 whitespace-nowrap">
                    <i className="ri-download-line mr-2"></i>
                    Exporter
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-6">Position de Trésorerie</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-blue-200">
                      <div className="flex items-center">
                        <i className="ri-money-euro-circle-line text-green-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Solde Caisse</span>
                      </div>
                      <span className="text-lg font-bold text-green-600">
                        {treasuryReport.cashBalance.toFixed(2)} GNF
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-blue-200">
                      <div className="flex items-center">
                        <i className="ri-bank-line text-blue-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Solde Bancaire Théorique</span>
                      </div>
                      <span className="text-lg font-bold text-blue-600">
                        {treasuryReport.bankBalance.toFixed(2)} GNF
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-blue-200">
                      <div className="flex items-center">
                        <i className="ri-arrow-up-circle-line text-orange-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Créances Clients à Recevoir</span>
                      </div>
                      <span className="text-lg font-bold text-orange-600">
                        +{treasuryReport.clientReceivables.toFixed(2)} GNF
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <div className="flex items-center">
                        <i className="ri-arrow-down-circle-line text-red-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Dettes Fournisseurs à Payer</span>
                      </div>
                      <span className="text-lg font-bold text-red-600">
                        -{treasuryReport.supplierPayables.toFixed(2)} GNF
                      </span>
                    </div>
                    <div className="pt-4 border-t-2 border-purple-300">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900">Trésorerie Nette</span>
                        <span className="text-2xl font-bold text-purple-600">
                          {treasuryReport.netTreasury.toFixed(2)} GNF
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-6">Analyse de Liquidité</h4>
                  <div className="space-y-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Liquidité Immédiate</p>
                          <p className="text-xs text-green-600">Caisse + Banque</p>
                        </div>
                        <p className="text-xl font-bold text-green-700">
                          {(treasuryReport.cashBalance + treasuryReport.bankBalance).toFixed(2)} GNF
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Besoins en Fonds de Roulement</p>
                          <p className="text-xs text-blue-600">Créances - Dettes</p>
                        </div>
                        <p className="text-xl font-bold text-blue-700">
                          {(treasuryReport.clientReceivables - treasuryReport.supplierPayables).toFixed(2)} GNF
                        </p>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-800">Ratio de Solvabilité</p>
                          <p className="text-xs text-purple-600">Actifs / Passifs</p>
                        </div>
                        <p className="text-xl font-bold text-purple-700">
                          {treasuryReport.supplierPayables > 0 
                            ? ((treasuryReport.cashBalance + treasuryReport.bankBalance + treasuryReport.clientReceivables) / treasuryReport.supplierPayables).toFixed(2)
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">Délai de Règlement Moyen</p>
                          <p className="text-xs text-gray-600">Estimation clients</p>
                        </div>
                        <p className="text-xl font-bold text-gray-700">32 jours</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graphique d'évolution - exemple avec données réelles */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-6">Évolution de la Trésorerie (6 derniers mois)</h4>
                <div className="h-64 flex items-end justify-between space-x-2 border-l border-b border-gray-200 pl-4 pb-4">
                  {[18500, 19200, 18800, 20100, 19600, treasuryReport.netTreasury].map((value, index) => {
                    const height = (value / 22000) * 100;
                    const months = ['Août', 'Sept', 'Oct', 'Nov', 'Déc', 'Jan'];
                    return (
                      <div key={index} className="flex flex-col items-center flex-1">
                        <div 
                          className="w-full bg-gradient-to-t from-purple-500 to-purple-300 rounded-t min-h-[20px]"
                          style={{ height: `${height}%` }}
                          title={`${value.toFixed(2)} GNF`}
                        ></div>
                        <span className="text-xs text-gray-600 mt-2">{months[index]}</span>
                        <span className="text-xs font-medium text-gray-900">{value.toFixed(0)} GNF</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* === MARGES === */}
          {activeTab === 'margins' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Rapport de Marge Brute</h3>
                <button className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  Exporter Analyse
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 text-green-600">
                      <i className="ri-money-euro-box-line text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Marge Brute Totale</p>
                      <p className="text-2xl font-bold text-green-600">{totalGrossMargin.toFixed(2)} GNF</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                      <i className="ri-percentage-line text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Marge Moyenne</p>
                      <p className="text-2xl font-bold text-blue-600">{averageMarginPercentage.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                      <i className="ri-trophy-line text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Meilleure Marge</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {marginReports.length > 0 ? Math.max(...marginReports.map(r => r.marginPercentage)).toFixed(1) : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix de Vente HT
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix d'Achat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Marge Unitaire
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % Marge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qté Vendue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Marge Totale
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {marginReports
                      .sort((a, b) => b.marginPercentage - a.marginPercentage)
                      .map((report) => (
                      <tr key={report.productId}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{report.productName}</div>
                          <div className="text-sm text-gray-500">ID: {report.productId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.salesPrice.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.purchasePrice.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          +{report.grossMargin.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            report.marginPercentage >= 35 ? 'bg-green-100 text-green-800' :
                            report.marginPercentage >= 25 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {report.marginPercentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.quantitySold}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          {report.totalMargin.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  report.marginPercentage >= 35 ? 'bg-green-500' :
                                  report.marginPercentage >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(report.marginPercentage * 2, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">
                              {report.marginPercentage >= 35 ? 'Excellent' :
                               report.marginPercentage >= 25 ? 'Correct' : 'Faible'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Graphique répartition marges */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-6">Répartition de la Marge par Produit</h4>
                <div className="h-64 flex items-end justify-between space-x-2 border-l border-b border-gray-200 pl-4 pb-4">
                  {marginReports.map((report, index) => {
                    const maxMargin = Math.max(...marginReports.map(r => r.totalMargin), 1);
                    const height = (report.totalMargin / maxMargin) * 100;
                    const colors = ['bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-indigo-500'];
                    return (
                      <div key={report.productId} className="flex flex-col items-center flex-1">
                        <div 
                          className={`w-full ${colors[index % colors.length]} rounded-t min-h-[20px]`}
                          style={{ height: `${height}%` }}
                          title={`${report.productName}: ${report.totalMargin.toFixed(2)} GNF`}
                        ></div>
                        <span className="text-xs text-gray-600 mt-2 transform -rotate-45 whitespace-nowrap">
                          {report.productName.substring(0, 10)}...
                        </span>
                        <span className="text-xs font-medium text-gray-900">{report.totalMargin.toFixed(0)} GNF</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* === JOURNAUX === */}
          {activeTab === 'journals' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Journaux Comptables</h3>

              {/* Journal des Achats */}
              <div className="bg-white border rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-medium text-gray-900">Journal des Achats</h4>
                    <button className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 whitespace-nowrap">
                      <i className="ri-download-line mr-2"></i>
                      Exporter PDF
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Liste chronologique des factures fournisseurs enregistrées
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fournisseur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          N° Facture
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant HT
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          TVA
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant TTC
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchaseJournal.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(entry.date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {entry.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {entry.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.amountHT.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.tva.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {entry.amountTTC.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.paymentStatus === 'Payée' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {entry.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-red-50">
                      <tr>
                        <td colSpan={4} className="px-6 py-3 text-sm font-medium text-gray-900">
                          TOTAUX
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {purchaseJournal.reduce((sum, entry) => sum + entry.amountHT, 0).toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {purchaseJournal.reduce((sum, entry) => sum + entry.tva, 0).toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {purchaseJournal.reduce((sum, entry) => sum + entry.amountTTC, 0).toFixed(2)} GNF
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Journal des Ventes */}
              <div className="bg-white border rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-medium text-gray-900">Journal des Ventes</h4>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 whitespace-nowrap">
                      <i className="ri-download-line mr-2"></i>
                      Exporter PDF
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Liste chronologique des factures clients émises
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          N° Facture
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant HT
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          TVA
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant TTC
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {salesJournal.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(entry.date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {entry.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {entry.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.amountHT.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.tva.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {entry.amountTTC.toFixed(2)} GNF
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.paymentType === 'Comptant' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {entry.paymentType || 'Crédit'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.paymentStatus === 'Payée' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {entry.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-green-50">
                      <tr>
                        <td colSpan={4} className="px-6 py-3 text-sm font-medium text-gray-900">
                          TOTAUX
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {salesJournal.reduce((sum, entry) => sum + entry.amountHT, 0).toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {salesJournal.reduce((sum, entry) => sum + entry.tva, 0).toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-3 text-sm font-bold text-gray-900">
                          {salesJournal.reduce((sum, entry) => sum + entry.amountTTC, 0).toFixed(2)} GNF
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* === PRÉVISIONNEL === */}
          {activeTab === 'forecasting' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Prévisionnel des Flux de Trésorerie</h3>
                <button className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  Exporter Prévisions
                </button>
              </div>

              {/* Scénarios */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-green-900">Scénario Optimiste</h4>
                    <i className="ri-arrow-up-line text-2xl text-green-600"></i>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Taux de recouvrement: 95%</p>
                    <p className="text-sm text-gray-600">Délai de paiement: -5 jours</p>
                    <p className="text-2xl font-bold text-green-600">
                      +{(treasuryReport.netTreasury * 1.15).toFixed(2)} GNF
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-blue-900">Scénario Réaliste</h4>
                    <i className="ri-arrow-right-line text-2xl text-blue-600"></i>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Taux de recouvrement: 85%</p>
                    <p className="text-sm text-gray-600">Délai de paiement: Standard</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {treasuryReport.netTreasury.toFixed(2)} GNF
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-red-900">Scénario Pessimiste</h4>
                    <i className="ri-arrow-down-line text-2xl text-red-600"></i>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Taux de recouvrement: 70%</p>
                    <p className="text-sm text-gray-600">Délai de paiement: +10 jours</p>
                    <p className="text-2xl font-bold text-red-600">
                      {(treasuryReport.netTreasury * 0.75).toFixed(2)} GNF
                    </p>
                  </div>
                </div>
              </div>

              {/* Tableau prévisionnel */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entrées Prévues
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sorties Prévues
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Flux Net
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Solde Cumulé
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tendance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cashFlowForecasts.map((forecast, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(forecast.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          +{forecast.expectedInflows.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                          -{forecast.expectedOutflows.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={forecast.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {forecast.netFlow >= 0 ? '+' : ''}{forecast.netFlow.toFixed(2)} GNF
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          {forecast.cumulativeBalance.toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {forecast.netFlow > 0 && (
                              <i className="ri-arrow-up-line text-green-500 mr-1"></i>
                            )}
                            {forecast.netFlow < 0 && (
                              <i className="ri-arrow-down-line text-red-500 mr-1"></i>
                            )}
                            {forecast.netFlow === 0 && (
                              <i className="ri-subtract-line text-gray-500 mr-1"></i>
                            )}
                            <span className={`text-xs font-medium ${
                              forecast.netFlow > 0 ? 'text-green-600' : 
                              forecast.netFlow < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                              {forecast.netFlow > 0 ? 'Positive' : 
                               forecast.netFlow < 0 ? 'Négative' : 'Stable'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Graphique prévisionnel */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-6">Évolution Prévisionnelle de la Trésorerie</h4>
                <div className="h-64 flex items-end justify-between space-x-2 border-l border-b border-gray-200 pl-4 pb-4">
                  {cashFlowForecasts.map((forecast, index) => {
                    const maxBalance = Math.max(...cashFlowForecasts.map(f => f.cumulativeBalance), 1);
                    const height = (forecast.cumulativeBalance / maxBalance) * 100;
                    const color = forecast.netFlow >= 0 ? 'bg-green-500' : 'bg-red-500';
                    return (
                      <div key={index} className="flex flex-col items-center flex-1">
                        <div 
                          className={`w-full ${color} rounded-t min-h-[20px]`}
                          style={{ height: `${height}%` }}
                          title={`${new Date(forecast.date).toLocaleDateString('fr-FR')}: ${forecast.cumulativeBalance.toFixed(2)} GNF`}
                        ></div>
                        <span className="text-xs text-gray-600 mt-2">
                          {new Date(forecast.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                        </span>
                        <span className="text-xs font-medium text-gray-900">
                          {forecast.cumulativeBalance.toFixed(0)} GNF
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex justify-center space-x-6">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                    <span className="text-sm text-gray-600">Flux Positif</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                    <span className="text-sm text-gray-600">Flux Négatif</span>
                  </div>
                </div>
              </div>

              {/* Recommandations */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h4 className="text-lg font-medium text-yellow-800 mb-4">
                  <i className="ri-lightbulb-line mr-2"></i>
                  Recommandations d'Optimisation du BFR
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Négocier des délais de paiement fournisseurs plus longs</span>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Mettre en place des acomptes clients sur les grosses commandes</span>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Optimiser la rotation des stocks</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <i className="ri-alert-line text-orange-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Surveiller les créances de plus de 30 jours</span>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-alert-line text-orange-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Prévoir une ligne de crédit pour les périodes difficiles</span>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-alert-line text-orange-600 mt-1 mr-2"></i>
                      <span className="text-sm text-gray-700">Diversifier les modes de paiement clients</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}